import { type FC, useCallback, useState } from "react";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { AutoFollowPillButton } from "mds/components/AutoFollowPillButton";
import { PillButton, PillButtonProps } from "mds/components/PillButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentCourseId } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { viewStateActions } from "store/slices/view";
import { usePresentation } from "utils/presentation";
import { Link, useQueryParamsManager } from "utils/routing";
import { pageUrl, withQueryParams } from "utils/urls";

type AutoFollowButtensProps = {
  allowAutoFollow: boolean;
  screenSharing?: boolean;
};

export const AutoFollowButtons: FC<AutoFollowButtensProps> = ({
  allowAutoFollow,
  screenSharing = false,
}) => {
  const { canParticipateInCourse } = useCapabilities();
  const dispatch = useAppDispatch();
  const { setQueryParams } = useQueryParamsManager();
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const { presentedPageId } = usePresentation();
  const [dismissed, setDismissed] = useState(!allowAutoFollow);
  const showShortText = !useIsMdOrLarger();

  const eventPrefix = screenSharing ? "Screensharing" : "Presentation";

  const buttonProps = screenSharing
    ? {}
    : ({
        as: Link,
        to: withQueryParams(pageUrl(currentCourseId, presentedPageId), {
          [FOLLOWING_QUERY_PARAM]: "1",
        }),
      } as PillButtonProps<typeof Link>);

  const handleClick = useCallback(() => {
    if (screenSharing) {
      dispatch(viewStateActions.setViewingScreenshare(true));
      setQueryParams({ [FOLLOWING_QUERY_PARAM]: "1" });
    } else if (canParticipateInCourse) {
      dispatch(localStateActions.setIsSidebarOpen(false));
    }
  }, [dispatch, setQueryParams, screenSharing, canParticipateInCourse]);

  return dismissed ? (
    <PillButton
      eventName={`${eventPrefix} - Start following`}
      kind="banner-green"
      size="s"
      onClick={handleClick}
      {...buttonProps}
    >
      {showShortText ? t("main_header.follow_short") : t("main_header.follow")}
    </PillButton>
  ) : (
    <div className="flex items-center gap-1">
      <PillButton
        className="border border-green"
        eventName={`${eventPrefix} - Dismiss Auto-Follow`}
        kind="green"
        size="s"
        onClick={() => setDismissed(true)}
      >
        {t(showShortText ? "common.dismiss_short" : "common.dismiss")}
      </PillButton>

      <AutoFollowPillButton
        eventName={`${eventPrefix} - Start following`}
        size="s"
        onClick={handleClick}
        {...buttonProps}
      >
        {t(showShortText ? "main_header.start_following_short" : "main_header.start_following")}
      </AutoFollowPillButton>
    </div>
  );
};
